'use client';

import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { markWelcomeComplete } from '@/utils/onboarding/actions';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useState, useTransition } from 'react';
import { welcomeSteps } from './steps';

export default function WelcomeFlow() {
  const [visible, setVisible] = useState(true);
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [isPending, startTransition] = useTransition();

  const total = welcomeSteps.length;
  const step = welcomeSteps[index];
  const isLast = index === total - 1;

  const finish = useCallback(() => {
    startTransition(async () => {
      try {
        await markWelcomeComplete();
        setVisible(false);
      } catch (err) {
        console.error(err);
      }
    });
  }, []);

  const next = useCallback(() => {
    if (isLast) {
      finish();
      return;
    }
    setDirection(1);
    setIndex((i) => Math.min(i + 1, total - 1));
  }, [isLast, finish, total]);

  const prev = useCallback(() => {
    if (index === 0) return;
    setDirection(-1);
    setIndex((i) => Math.max(i - 1, 0));
  }, [index]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        finish();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, finish]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="welcome-flow"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1]
          }}
          className="fixed inset-0 z-[100] flex flex-col bg-white"
          role="dialog"
          aria-modal="true"
          aria-label="Welcome to Corvus"
        >
          <header className="flex items-center justify-between p-4 py-2">
            <img src="/brand/logo.svg" alt="Corvus" className="h-[20px]" />
            <Button
              type="button"
              variant="light"
              size="sm"
              onClick={finish}
              disabled={isPending}
            >
              Skip intro
            </Button>
          </header>

          <main className="flex flex-1 items-center justify-center px-8 pb-16">
            <div className="relative w-full max-w-xl overflow-hidden">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={index}
                  custom={direction}
                  initial={{ opacity: 0, y: direction * 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: direction * -12 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="text-center"
                >
                  <div className="mb-4 text-xs font-medium uppercase tracking-[0.18em] text-zinc-400">
                    {step.eyebrow}
                  </div>
                  <h1 className="text-balance text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">
                    {step.title}
                  </h1>
                  <p className="mx-auto mt-5 max-w-md text-pretty text-base leading-relaxed text-zinc-500">
                    {step.body}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>
          </main>

          <footer className="flex flex-col items-center gap-8 px-8 pb-6">
            <Button
              type="button"
              variant="dark"
              size="sm"
              onClick={next}
              disabled={isPending}
              className="rounded-full px-6"
            >
              {isPending && isLast ? 'Finishing…' : step.cta}
            </Button>
            <div className="flex items-center justify-center gap-2">
              {welcomeSteps.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Go to step ${i + 1}`}
                  onClick={() => {
                    setDirection(i > index ? 1 : -1);
                    setIndex(i);
                  }}
                  className="group p-1.5"
                >
                  <span
                    className={cn(
                      'block h-1.5 rounded-full transition-all duration-300 ease-out',
                      i === index
                        ? 'w-6 bg-zinc-900'
                        : 'w-1.5 bg-zinc-300 group-hover:bg-zinc-400'
                    )}
                  />
                </button>
              ))}
            </div>
          </footer>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
